import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { switchMap, catchError, map, pluck, withLatestFrom, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import * as fromActions from '../actions/delivery.actions';
import { DeliveryService } from 'app/core/services/delivery.service';
import { Delivery } from 'app/core/models';
import { AssetGroup, OfflineDelivery } from 'app/core/models/delivery.model';
import { selectDownloadedDelivery } from '../selectors/offline.selectors';
import { ConnectionService } from 'app/core/services/connection.service';
import { refresh } from 'app/inbox/inbox-deliveries/store/inbox.actions';

@Injectable()
export class DeliveryEffects {
  actions$ = inject(Actions);
  deliveryService = inject(DeliveryService);
  router = inject(Router);
  store = inject(Store);
  connectionService = inject(ConnectionService);

  loadDelivery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadDelivery),
      pluck('payload'),
      switchMap((deliveryId: string) => {
        if (!this.connectionService.isOnline) {
          return this.getOfflineDownloadedDelivery(deliveryId);
        }
        return this.getDeliveryFromRequest(deliveryId);
      }),
    ),
  );

  getDeliveryEmailText$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GetDeliveryEmailText),
      pluck('payload'),
      switchMap((deliveryId: string) =>
        this.deliveryService.getDeliveryEmailText(deliveryId).pipe(
          map((emailText: string) => fromActions.SetDeliveryEmailText({ payload: emailText })),
          catchError(() => of(fromActions.LoadDeliveryFail())),
        ),
      ),
    ),
  );

  getOfflineDownloadedDelivery(deliveryId) {
    return this.store.pipe(
      select(selectDownloadedDelivery, { deliveryKeyCode: deliveryId }),
      map((delivery: Delivery) => fromActions.LoadDeliverySuccess({ payload: delivery })),
      catchError(() => {
        this.router.navigate(['/']);
        return of(fromActions.LoadDeliveryFail());
      }),
    );
  }

  getDeliveryFromRequest(deliveryId) {
    return this.deliveryService.getDeliveryDetails(deliveryId).pipe(
      withLatestFrom(
        this.store.pipe(select(selectDownloadedDelivery, { deliveryKeyCode: deliveryId })),
      ),
      map(([delivery, offlineDelivery]) => {
        const resultDelivery = this.getResultDelivery(delivery, offlineDelivery);

        return fromActions.LoadDeliverySuccess({ payload: resultDelivery });
      }),
      catchError(() => {
        this.store.dispatch(refresh());
        this.router.navigate(['/']);
        return of(fromActions.LoadDeliveryFail());
      }),
    );
  }

  getResultDelivery(delivery, offlineDelivery: OfflineDelivery) {
    if (offlineDelivery && offlineDelivery.deliveryKeyCode) {
      const restOffline = { ...offlineDelivery };

      restOffline.assetGroups = delivery.assetGroups.map((group: AssetGroup) => {
        if (group.assetCategoryName === 'Audio') {
          // eslint-disable-next-line no-param-reassign
          group.numberOfAssets = restOffline.assetGroups[0].numberOfAssets;
        }
        return group;
      });
      return restOffline;
    }
    return delivery;
  }
}
